import { baseApi } from './baseApi';
import { Material } from 'models/Material';
import { CheckUsedResponse, QueryParams, ReplaceRequest, ReplaceResponse, TAG_TYPES } from 'types';
import { getQueryParams } from 'utils';

const URL_MATERIAL = 'materials';

const materialApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMaterials: builder.query<
      {
        data: {
          rows: Material[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_MATERIAL, method: 'GET' };
        return {
          url: `${URL_MATERIAL}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Material, id })),
              { type: TAG_TYPES.Material, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Material, id: 'LIST' }];
      },
    }),

    getMaterialById: builder.query<Material, string>({
      query: (id) => ({
        url: `${URL_MATERIAL}/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Material, id }],
    }),

    createMaterial: builder.mutation<Material, Omit<Material, 'id'>>({
      query: (data) => ({
        url: URL_MATERIAL,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Material, id: 'LIST' }],
    }),

    updateMaterial: builder.mutation<Material, Partial<Material> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${URL_MATERIAL}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Material, id },
        { type: TAG_TYPES.Material, id: 'LIST' },
      ],
    }),

    deleteMaterial: builder.mutation<void, string>({
      query: (id) => ({
        url: `${URL_MATERIAL}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Material, id },
        { type: TAG_TYPES.Material, id: 'LIST' },
      ],
    }),

    // Thêm API check usage cho Material
    checkMaterialUsage: builder.query<CheckUsedResponse, string>({
      query: (id) => ({
        url: `${URL_MATERIAL}/${id}/check-used`,
        method: 'POST',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Material, id: `CHECK_USED_${id}` }],
    }),

    deactivateMaterial: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_MATERIAL}/${id}/deactivate`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Material, id },
        { type: TAG_TYPES.Material, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Category, id: 'LIST' },
      ],
    }),

    deleteMaterialWithReplacement: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_MATERIAL}/${id}/delete`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Material, id },
        { type: TAG_TYPES.Material, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Category, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMaterialsQuery: useGetAllMaterialsQuery,
  useLazyGetMaterialsQuery: useLazyGetAllMaterialsQuery,
  useGetMaterialByIdQuery,
  useCreateMaterialMutation,
  useUpdateMaterialMutation,
  useDeleteMaterialMutation,
  useLazyCheckMaterialUsageQuery,
  useDeactivateMaterialMutation,
  useDeleteMaterialWithReplacementMutation,
} = materialApiService;
